/**
 * AdminBackgroundCheckDetail — /admin/background-checks/[id]
 * Role-aware detail view. Support sees list-view; Compliance sees the
 * full consent record + adjudication + adverse-action state + Checkr
 * dashboard link + Send Candidate Copy button.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, ExternalLink, Loader2, CheckCircle2, FileSignature, AlertTriangle, Send, Clock, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Row = ({ label, children, testId }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-border last:border-b-0 text-sm">
        <dt className="text-muted-foreground">{label}</dt>
        <dd className="col-span-2 text-foreground" data-testid={testId}>{children}</dd>
    </div>
);

const StatusChip = ({ label, status }) => (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        status === "clear" ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40"
        : ["consider", "consider_review_required"].includes(status) ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40"
        : status === "dispute" ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40"
        : "bg-muted text-foreground border-border"
    }`}>
        {label || status}
    </span>
);

export default function AdminBackgroundCheckDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendingCopy, setSendingCopy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/background-checks/${id}`);
            setRow(data);
        } catch (err) {
            const msg = err?.response?.status === 404
                ? "Background check not found."
                : err?.response?.data?.detail || "Failed to load.";
            toast.error(msg);
            setRow(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const sendCandidateCopy = async () => {
        setSendingCopy(true);
        try {
            await api.post(`/admin/background-checks/${id}/candidate-copy`, {});
            toast.success("Candidate copy marked as sent.");
            load();
        } catch (err) {
            const msg = err?.response?.status === 403
                ? "Only Compliance-role admins can mark candidate copies sent."
                : err?.response?.data?.detail || "Failed to send.";
            toast.error(msg);
        } finally {
            setSendingCopy(false);
        }
    };

    if (loading) {
        return <div className="container-app py-14 flex justify-center"><Loader2 className="animate-spin text-emerald-500" /></div>;
    }
    if (!row) {
        return (
            <div className="container-app py-14">
                <div className="card-soft p-8 max-w-lg mx-auto text-center" data-testid="bg-detail-404">
                    <AlertTriangle size={22} className="mx-auto text-amber-500" />
                    <div className="mt-3 font-heading font-semibold text-xl text-foreground">Background check not found</div>
                    <p className="mt-2 text-sm text-muted-foreground">This record may have been purged, or you may not have permission to view it.</p>
                    <button type="button" onClick={() => navigate("/admin/background-checks")} className="mt-5 btn-outline">Back to list</button>
                </div>
            </div>
        );
    }

    const consent = row.consent_record;
    const isCompliance = !!consent;
    const aa = row.adverse_action;
    const dispute = row.dispute;

    return (
        <div className="container-app py-10" data-testid="bg-detail-page">
            <div className="flex items-center gap-2 text-sm">
                <Link to="/admin/background-checks" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="bg-detail-back">
                    <ArrowLeft size={14} /> All background checks
                </Link>
            </div>

            {/* Header */}
            <header className="mt-4 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Background check</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        {row.candidate_name || row.candidate_email}
                    </h1>
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-sm">
                        <StatusChip label={row.result_label} status={row.status} />
                        <span className="text-muted-foreground">·</span>
                        <span className="text-foreground">{row.package_label || row.package_id}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">Requested {row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}</span>
                    </div>
                </div>
                {isCompliance && row.checkr_dashboard_url && (
                    <a href={row.checkr_dashboard_url} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-detail-checkr-link">
                        Full report in Checkr <ExternalLink size={11} />
                    </a>
                )}
            </header>

            {!isCompliance && (
                <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-800 dark:text-amber-300" data-testid="bg-detail-support-notice">
                    <b>Support view.</b> Consent records, adjudication details, and Checkr dashboard access are gated to Compliance-role admins.
                </div>
            )}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left column — main data */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Summary */}
                    <section className="card-soft p-5" data-testid="bg-detail-summary">
                        <h2 className="font-heading font-semibold text-lg text-foreground">Summary</h2>
                        <dl className="mt-3">
                            <Row label="Candidate" testId="bg-detail-candidate-name">
                                <Link to={`/admin/users/${row.candidate_id || ""}`} className="text-emerald-700 dark:text-emerald-400 hover:underline">{row.candidate_name || row.candidate_email}</Link>
                            </Row>
                            <Row label="Employer">{row.employer_company || "—"}</Row>
                            <Row label="Package">{row.package_label || row.package_id}</Row>
                            <Row label="Status"><StatusChip label={row.result_label} status={row.status} /></Row>
                            <Row label="Requested">{row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}</Row>
                            <Row label="Completed">{row.completed_at ? new Date(row.completed_at).toLocaleString() : "—"}</Row>
                            {row.processing_started_at && row.completed_at && (
                                <Row label="Turnaround">
                                    {Math.round((new Date(row.completed_at) - new Date(row.processing_started_at)) / 3600000)} hours
                                </Row>
                            )}
                            <Row label="Candidate copy sent">
                                {row.candidate_copy_sent ? (
                                    <span className="text-emerald-600 dark:text-emerald-400 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={12} /> Yes {row.candidate_copy_sent_at && <span className="text-xs text-muted-foreground">({new Date(row.candidate_copy_sent_at).toLocaleDateString()})</span>}</span>
                                ) : "No"}
                            </Row>
                        </dl>
                    </section>

                    {/* Consent record (Compliance only) */}
                    {isCompliance && (
                        <section className="card-soft p-5" data-testid="bg-detail-consent">
                            <div className="flex items-center gap-2">
                                <FileSignature size={16} className="text-emerald-500" />
                                <h2 className="font-heading font-semibold text-lg text-foreground">Consent record</h2>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">The legal backbone. Never edit — only append.</p>
                            <dl className="mt-3">
                                <Row label="Consent signed">{consent.consent_at ? new Date(consent.consent_at).toLocaleString() : "—"}</Row>
                                <Row label="Method">{consent.consent_method}</Row>
                                <Row label="Disclosure version"><code className="text-xs">{consent.consent_disclosure_version || "—"}</code></Row>
                                <Row label="Authorization version"><code className="text-xs">{consent.consent_authorization_version || "—"}</code></Row>
                                <Row label="Signature">{consent.consent_signature_masked || "—"}</Row>
                                <Row label="IP address"><code className="text-xs">{consent.consent_ip || "—"}</code></Row>
                            </dl>
                        </section>
                    )}

                    {/* Fair Chance attestation */}
                    {isCompliance && row.btb_attestation && (
                        <section className="card-soft p-5" data-testid="bg-detail-btb">
                            <h2 className="font-heading font-semibold text-lg text-foreground">Fair Chance attestation</h2>
                            <p className="mt-1 text-xs text-muted-foreground">Employer confirmed a conditional offer was extended before ordering.</p>
                            <dl className="mt-3">
                                <Row label="Accepted">{row.btb_attestation.accepted ? "Yes" : "No"}</Row>
                                <Row label="Version"><code className="text-xs">{row.btb_attestation.version}</code></Row>
                                <Row label="By">{row.btb_attestation.accepted_by || "—"}</Row>
                                <Row label="At">{row.btb_attestation.accepted_at ? new Date(row.btb_attestation.accepted_at).toLocaleString() : "—"}</Row>
                            </dl>
                        </section>
                    )}

                    {/* Adjudication */}
                    {isCompliance && (row.adjudication || row.assessment) && (
                        <section className="card-soft p-5" data-testid="bg-detail-adjudication">
                            <h2 className="font-heading font-semibold text-lg text-foreground">Adjudication</h2>
                            <dl className="mt-3">
                                {row.assessment && (
                                    <Row label="Checkr Assess">
                                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                                            row.assessment === "review" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40" : "bg-slate-500/15 text-slate-600 border-slate-500/40"
                                        }`}>{row.assessment}</span>
                                    </Row>
                                )}
                                {row.adjudication?.guidelines_applied && <Row label="Guidelines">{(row.adjudication.guidelines_applied || []).join(", ") || "—"}</Row>}
                                {row.adjudication?.decision && <Row label="Decision">{row.adjudication.decision}</Row>}
                            </dl>
                            <p className="mt-3 text-xs text-muted-foreground">Full report details live in the Checkr dashboard — Simonara never stores report PDFs.</p>
                        </section>
                    )}

                    {/* Dispute */}
                    {dispute && (
                        <section className="card-soft p-5 border-red-500/40" data-testid="bg-detail-dispute">
                            <div className="flex items-center gap-2">
                                <Flag size={16} className="text-red-500" />
                                <h2 className="font-heading font-semibold text-lg text-foreground">Dispute</h2>
                            </div>
                            <dl className="mt-3">
                                <Row label="Status">{dispute.status || "open"}</Row>
                                <Row label="Opened">{dispute.opened_at ? new Date(dispute.opened_at).toLocaleString() : "—"}</Row>
                                {isCompliance && dispute.description && <Row label="Candidate reason">{dispute.description}</Row>}
                                {isCompliance && dispute.outcome && <Row label="Outcome">{dispute.outcome}</Row>}
                                {isCompliance && dispute.resolution_notes && <Row label="Resolution notes">{dispute.resolution_notes}</Row>}
                            </dl>
                        </section>
                    )}
                </div>

                {/* Right column — actions + adverse-action state */}
                <aside className="space-y-5">
                    {/* Send candidate copy */}
                    {isCompliance && (
                        <section className="card-soft p-5" data-testid="bg-detail-candidate-copy-panel">
                            <h3 className="font-heading font-semibold text-base text-foreground">Candidate copy</h3>
                            <p className="mt-1 text-xs text-muted-foreground">Mark a candidate copy as sent for the audit trail. Checkr delivers the actual copy automatically when <code>copy_requested</code> is set on the invitation.</p>
                            <button
                                type="button"
                                onClick={sendCandidateCopy}
                                disabled={sendingCopy || row.candidate_copy_sent}
                                className="mt-4 w-full btn-green inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="bg-detail-send-copy-btn"
                            >
                                {sendingCopy ? <><Loader2 size={12} className="animate-spin" /> Marking…</> : row.candidate_copy_sent ? <><CheckCircle2 size={12} /> Already sent</> : <><Send size={12} /> Mark copy sent</>}
                            </button>
                        </section>
                    )}

                    {/* Adverse action */}
                    {isCompliance && aa && (
                        <section className="card-soft p-5" data-testid="bg-detail-adverse-action">
                            <div className="flex items-center gap-2">
                                <Clock size={14} className="text-amber-500" />
                                <h3 className="font-heading font-semibold text-base text-foreground">Adverse action</h3>
                            </div>
                            <dl className="mt-3 text-sm">
                                <Row label="Status">
                                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                                        aa.status === "completed" ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/40"
                                        : ["canceled", "cancelled"].includes(aa.status) ? "bg-muted text-muted-foreground border-border"
                                        : "bg-amber-500/15 text-amber-700 border-amber-500/40"
                                    }`}>{aa.status || "created"}</span>
                                </Row>
                                <Row label="Pre-notice">{aa.pre_notice_date ? new Date(aa.pre_notice_date).toLocaleDateString() : "—"}</Row>
                                <Row label="Post-notice">{aa.post_notice_date ? new Date(aa.post_notice_date).toLocaleDateString() : "—"}</Row>
                                <Row label="Reviewer">{aa.reviewer_email || "—"}</Row>
                            </dl>
                            {aa.checkr_adverse_action_id && (
                                <div className="mt-3 text-[11px] text-muted-foreground">
                                    Checkr id: <code>{aa.checkr_adverse_action_id}</code>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Meta */}
                    <section className="card-soft p-5 text-xs text-muted-foreground" data-testid="bg-detail-meta">
                        <div><b>Record id:</b> <code>{row.id}</code></div>
                        {row.checkr_report_id && <div className="mt-1"><b>Checkr report id:</b> <code>{row.checkr_report_id}</code></div>}
                        {row.checkr_candidate_id && <div className="mt-1"><b>Checkr candidate id:</b> <code>{row.checkr_candidate_id}</code></div>}
                    </section>
                </aside>
            </div>
        </div>
    );
}
